import {memo} from 'react';
import {Badge} from '@/components/ui/badge';
import {Campaign} from '../../types';
import {fmt} from '@/pages/Dashboard';

interface CampaignListItemProps {
    campaign: Campaign;
    onClick: () => void;
    variant: 'tracked' | 'untracked';
    visibleColumns: {
        id: boolean;
        status: boolean;
        objective: boolean;
        social_spend_brl: boolean;
        social_spend_usd: boolean;
        social_impressions: boolean;
        social_clicks: boolean;
        social_reach: boolean;
        social_cpc: boolean;
        social_cpm: boolean;
        social_ctr: boolean;
        social_conversions: boolean;
        revenue: boolean;
        revenue_impressions: boolean;
        revenue_clicks: boolean;
        revenue_ecpm: boolean;
        revenue_requests_served: boolean;
        revenue_elegible_ad_request: boolean;
        revenue_pmr: boolean;
        revenue_ctr: boolean;
        roi: boolean;
        profit: boolean;
        dolar_rate: boolean;
    };
}

function CampaignListItemComponent({campaign, onClick, variant, visibleColumns}: CampaignListItemProps) {
    const isGroup = campaign.is_group || false;

    // Helper para badge de tracking
    const getTrackingBadge = () => {
        if (variant === 'untracked') {
            return {label: 'Sem Tracking', color: 'bg-orange-600 text-white'};
        }

        const level = campaign.custom_tracking || campaign.tracking_level;
        switch (level) {
            case 'ad':
                return {label: 'Ad', color: 'bg-pink-600 text-white'};
            case 'adset':
                return {label: 'Adset', color: 'bg-purple-600 text-white'};
            case 'campaign':
                return {label: 'Campaign', color: 'bg-blue-600 text-white'};
            default:
                return {label: 'Campaign', color: 'bg-blue-600 text-white'};
        }
    };

    const trackingBadge = getTrackingBadge();
    const hoverBgColor = variant === 'tracked' ? 'hover:bg-blue-50/50' : 'hover:bg-orange-50/50';
    const arrowHoverColor = variant === 'tracked' ? 'group-hover:text-blue-600' : 'group-hover:text-orange-600';

    return (
        <tr
            onClick={onClick}
            className={`border-b border-gray-200 cursor-pointer transition-colors group ${
                isGroup
                    ? 'bg-indigo-50/30 hover:bg-indigo-50/70'
                    : hoverBgColor
            }`}
        >
            {/* Coluna de Informações */}
            <td className={`p-3 border-r ${
                isGroup ? 'bg-indigo-50/50 border-indigo-200' : 'bg-amber-50/30 border-amber-200'
            }`}>
                {/* Badges */}
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    {isGroup ? (
                        <>
                            <Badge className="bg-indigo-600 text-white flex-shrink-0"
                                   style={{fontSize: '9px', padding: '2px 6px'}}>
                                📦 Grupo
                            </Badge>

                            <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200"
                                   style={{fontSize: '9px', padding: '2px 6px'}}>
                                {campaign.entities_count} entidades
                            </Badge>
                        </>
                    ) : (
                        <>
                            <Badge className={`${trackingBadge.color} flex-shrink-0`}
                                   style={{fontSize: '9px', padding: '2px 6px'}}>
                                {trackingBadge.label}
                            </Badge>
                        </>
                    )}

                    {visibleColumns.status && campaign.status && (
                        <Badge
                            className={campaign.status === 'ACTIVE' ? 'bg-green-600 text-white' : 'bg-gray-500 text-white'}
                            style={{fontSize: '9px', padding: '2px 6px'}}
                        >
                            {campaign.status === 'ACTIVE' ? 'Ativo' : 'Pausado'}
                        </Badge>
                    )}

                    {visibleColumns.objective && campaign.objective && (
                        <Badge
                            className="bg-purple-100 text-purple-700 border border-purple-200"
                            style={{fontSize: '9px', padding: '2px 6px'}}
                        >
                            🎯 {campaign.objective}
                        </Badge>
                    )}
                </div>

                {/* Nome */}
                <div className="font-medium text-gray-900 text-sm mb-1.5 leading-tight">
                    {isGroup
                        ? campaign.group_name
                        : (campaign.campaign_name || campaign.adset_name || campaign.ad_name)
                    }
                </div>

                {/* Info Row */}
                <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                    {isGroup ? (
                        <>
                            <span className="flex items-center gap-1">
                                <span className="text-indigo-500">📋</span>
                                <span className="text-indigo-600 font-medium">
                                    {campaign.entity_types?.join(', ')}
                                </span>
                            </span>

                            {campaign.custom_tracking && (
                                <>
                                    <span className="text-gray-300">•</span>
                                    <span className="bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200">
                                        <span className="text-indigo-600 font-medium">UTM:</span>
                                        <span
                                            className="text-indigo-700 font-mono ml-1">{campaign.custom_tracking}</span>
                                    </span>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                        <span className="flex items-center gap-1">
                        <span className="text-gray-400">🌐</span>
                        <span>{campaign.domain}</span>
                        </span>
                            {visibleColumns.id && campaign.id && (
                                <>
                                    <span className="text-gray-300">•</span>
                                    <span>ID: {campaign.id}</span>
                                </>
                            )}

                            {campaign.custom_tracking && (
                                <>
                                    <span className="text-gray-300">•</span>
                                    <span className="bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                                <span className="text-blue-600 font-medium">UTM:</span>
                                <span className="text-blue-700 font-mono ml-1">{campaign.custom_tracking}</span>
                            </span>
                                </>
                            )}
                        </>
                    )}

                </div>
            </td>

            {/* Colunas Social */}
            {visibleColumns.social_spend_brl && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.currencyBRL(campaign.social_spend_brl || 0)}
                </td>
            )}
            {visibleColumns.social_spend_usd && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.currency(campaign.social_spend_usd || 0)}
                </td>
            )}
            {visibleColumns.social_impressions && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.number(campaign.social_impressions || 0)}
                </td>
            )}
            {visibleColumns.social_clicks && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.number(campaign.social_clicks || 0)}
                </td>
            )}
            {visibleColumns.social_reach && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.number(campaign.social_reach || 0)}
                </td>
            )}
            {visibleColumns.social_cpc && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.currencyBRL(campaign.social_cpc || 0)}
                </td>
            )}
            {visibleColumns.social_cpm && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.currencyBRL(campaign.social_cpm || 0)}
                </td>
            )}
            {visibleColumns.social_ctr && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.percent(campaign.social_ctr || 0)}
                </td>
            )}
            {visibleColumns.social_conversions && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-blue-50/30 border-r border-blue-100 whitespace-nowrap">
                    {fmt.number(campaign.social_conversions || 0)}
                </td>
            )}

            {/* Colunas Revenue (apenas para tracked) */}
            {variant === 'tracked' && visibleColumns.revenue && (
                <td className="p-3 text-right text-sm font-semibold text-green-700 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.currency(campaign.revenue || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.revenue_impressions && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.number(campaign.revenue_impressions || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.revenue_clicks && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.number(campaign.revenue_clicks || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.revenue_ecpm && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.currency(campaign.revenue_ecpm || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.revenue_requests_served && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.number(campaign.revenue_requests_served || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.revenue_elegible_ad_request && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.number(campaign.revenue_elegible_ad_request || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.revenue_pmr && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.percent(campaign.revenue_pmr || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.revenue_ctr && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-green-50/30 border-r border-green-100 whitespace-nowrap">
                    {fmt.percent(campaign.revenue_ctr || 0)}
                </td>
            )}

            {/* Colunas Performance (apenas para tracked) */}
            {variant === 'tracked' && visibleColumns.roi && (
                <td className={`p-3 text-right text-sm font-semibold bg-purple-50/30 border-r border-purple-100 whitespace-nowrap ${(campaign.roi || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {fmt.percent(campaign.roi || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.profit && (
                <td className={`p-3 text-right text-sm font-semibold bg-purple-50/30 border-r border-purple-100 whitespace-nowrap ${(campaign.profit || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {fmt.currency(campaign.profit || 0)}
                </td>
            )}
            {variant === 'tracked' && visibleColumns.dolar_rate && (
                <td className="p-3 text-right text-sm font-semibold text-gray-900 bg-purple-50/30 border-r border-purple-100 whitespace-nowrap">
                    R$ {(campaign.dolar_rate || 0).toFixed(2)}
                </td>
            )}

            {/* Coluna de Status (apenas para untracked) */}
            {variant === 'untracked' && (
                <td className="p-3 text-center bg-orange-50/30 border-r border-orange-100">
                    <span className="text-xs font-semibold text-orange-600">⚠️ Configurar</span>
                </td>
            )}

            {/* Coluna de Ação */}
            <td className="p-3">
                <svg className={`w-5 h-5 text-gray-400 ${arrowHoverColor} transition-colors`} fill="none"
                     viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                </svg>
            </td>
        </tr>
    );
}

// Memoização com comparação customizada para evitar re-renders desnecessários
export const CampaignListItem = memo(CampaignListItemComponent, (prevProps, nextProps) => {
    // Retorna true se as props são iguais (não deve re-renderizar)
    // Retorna false se as props mudaram (deve re-renderizar)

    // Se o campaign mudou (por ID), precisa re-renderizar
    if (prevProps.campaign.id !== nextProps.campaign.id) return false;

    // Se o variant mudou, precisa re-renderizar
    if (prevProps.variant !== nextProps.variant) return false;

    // Comparação shallow de visibleColumns (mais eficiente que deep comparison)
    const prevKeys = Object.keys(prevProps.visibleColumns).filter(k => prevProps.visibleColumns[k as keyof typeof prevProps.visibleColumns]);
    const nextKeys = Object.keys(nextProps.visibleColumns).filter(k => nextProps.visibleColumns[k as keyof typeof nextProps.visibleColumns]);

    if (prevKeys.length !== nextKeys.length) return false;
    if (prevKeys.some((k, i) => k !== nextKeys[i])) return false;

    // Se tudo é igual, não precisa re-renderizar
    return true;
});